class PageTransitions {
    constructor() {
        this.transitionOverlay = document.querySelector('.page-transition-overlay');
        this.fadeCheckInterval = null;
        this.isMobile = this.checkIfMobile();
        this.pageVisible = true;
        
        this.init();
    }

    checkIfMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    init() {
        // Handle internal link clicks
        this.handleInternalLinks();
        
        // Handle browser back/forward navigation
        this.handleBrowserNavigation();
        
        // Handle page load - this should happen for ALL page loads
        this.handlePageLoad();
        
        // Add visibility change listener for better mobile handling
        this.handleVisibilityChange();
    }

    handleInternalLinks() {
        // First, handle all anchor links specifically to prevent transitions
        const anchorLinks = document.querySelectorAll('a[href^="#"]');
        anchorLinks.forEach(link => {
            // For anchor links, we don't want any transition behavior
            link.addEventListener('click', (e) => {
                // Only handle if it's a real anchor link (not something else with # like #!)
                const href = link.getAttribute('href');
                if (href && href.startsWith('#') && href.length > 1) {
                    // Allow default behavior for normal anchor links
                    // For back to top links specifically, add smooth scroll
                    e.preventDefault();
                    const targetId = href.substring(1);
                    const targetElement = document.getElementById(targetId);
                    if (targetElement) {
                        targetElement.scrollIntoView({ behavior: 'smooth' });
                    }
                }
            });
        });

        // Now handle internal links that are not anchor links
        // First get all links
        const allLinks = document.querySelectorAll('a');
        
        allLinks.forEach(link => {
            const href = link.getAttribute('href');
            if (!href) return;
            
            // Skip if it's an anchor link or has no-href attribute
            if (href.startsWith('#') || link.hasAttribute('no-href')) return;
            
            // Only proceed if it's an internal link (starts with / or origin)
            // if (!href.startsWith('/') && !href.startsWith(window.location.origin)) return;
            
            link.addEventListener('click', (e) => {
                // Skip if it's a download link or has a target
                if (link.hasAttribute('download') || link.target === '_blank') return;
                
                // Check for keyboard modifiers (Command/Ctrl for new tab, Shift for new window)
                if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                
                // Prevent default behavior
                e.preventDefault();
                
                // Fade out
                this.fadeOut(() => {
                    // Navigate to the new page
                    window.location.href = href;
                });
            });
        });
    }

    handleBrowserNavigation() {
        // Listen for popstate events (browser back/forward buttons)
        window.addEventListener('popstate', () => {
            
            // Ensure the overlay is always visible when navigating with back/forward
            if (this.transitionOverlay) {
                this.transitionOverlay.style.opacity = '1';
                this.transitionOverlay.style.display = 'block';
            }
            
            // Force a fade in to happen
            this.fadeIn();
            this.ensureOverlayFaded();
        });
    }
    
    handleVisibilityChange() {
        // This helps catch mobile back button events that might not trigger popstate
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.pageVisible = true;
                
                // Only run this on mobile devices since desktop is working well
                if (this.isMobile && this.transitionOverlay) {
                    // Force overlay to be visible then quickly fade it out
                    this.transitionOverlay.style.opacity = '1';
                    this.transitionOverlay.style.display = 'block';
                    
                    // Trigger the fade in immediately
                    setTimeout(() => {
                        this.fadeIn();
                        this.ensureOverlayFaded();
                    }, 50);
                }
            } else {
                this.pageVisible = false;
            }
        });
    }

    handlePageLoad() {
        // This method should ensure the overlay fades out on EVERY page load
        if (!this.transitionOverlay) return;
        
        // Use requestAnimationFrame to ensure the browser has painted the initial state
        requestAnimationFrame(() => {
            // Add a small delay to ensure rendering is complete
            setTimeout(() => {
                // Explicitly fade in the content
                this.fadeIn();
                // Add safety check for all page loads
                this.ensureOverlayFaded();
            }, 100);
        });
    }

    fadeOut(callback) {
        if (!this.transitionOverlay) return;
        
        // Set initial state
        this.transitionOverlay.style.opacity = '0';
        this.transitionOverlay.style.display = 'block';
        
        // Use requestAnimationFrame to ensure the browser has painted
        requestAnimationFrame(() => {
            // Animate to visible
            gsap.to(this.transitionOverlay, {
                opacity: 1,
                duration: 0.5,
                ease: 'power2.inOut',
                onComplete: () => {
                    if (callback) callback();
                }
            });
        });
    }

    fadeIn() {
        if (!this.transitionOverlay) return;
        
        // Set initial state explicitly
        this.transitionOverlay.style.opacity = '1';
        // Set initial state explicitly
        this.transitionOverlay.style.opacity = '1';
        this.transitionOverlay.style.display = 'block';
        
        // For mobile, force immediate setting through direct style manipulation first
        if (this.isMobile) {
            // Force a browser repaint to ensure styles are applied
            void this.transitionOverlay.offsetWidth;
        }
        
        // Use requestAnimationFrame to ensure the browser has painted
        requestAnimationFrame(() => {
            // Animate to invisible
            gsap.to(this.transitionOverlay, {
                opacity: 0,
                duration: 0.5,
                ease: 'power2.inOut',
                onComplete: () => {
                    // Ensure overlay is hidden
                    this.transitionOverlay.style.display = 'none';
                    this.transitionOverlay.style.opacity = '0';
                    
                    // Remove the transition class
                    document.body.classList.remove('page-transitioning');
                    
                    // Dispatch event
                    document.dispatchEvent(new CustomEvent('pageTransitionComplete'));
                }
            });
        });
    }
    
    ensureOverlayFaded() {
        // Clear any existing interval
        if (this.fadeCheckInterval) {
            clearInterval(this.fadeCheckInterval);
        }
        
        if (!this.transitionOverlay) return;
        
        // Set a maximum check time (5 seconds)
        const maxCheckTime = 5000;
        const startTime = Date.now();
        
        // Check every 100ms if the overlay is faded out
        this.fadeCheckInterval = setInterval(() => {
            if (!this.transitionOverlay) {
                clearInterval(this.fadeCheckInterval);
                this.fadeCheckInterval = null;
                return;
            }
            
            const currentOpacity = parseFloat(window.getComputedStyle(this.transitionOverlay).opacity);
            const elapsedTime = Date.now() - startTime;
            
            // If opacity is 0 or very close to it, or display is none, consider it faded
            if (currentOpacity < 0.1 || this.transitionOverlay.style.display === 'none') {
                clearInterval(this.fadeCheckInterval);
                this.fadeCheckInterval = null;
                
                // Ensure all properties are correctly set
                this.transitionOverlay.style.opacity = '0';
                this.transitionOverlay.style.display = 'none';
                document.body.classList.remove('page-transitioning');
            }
            // If we've exceeded the maximum check time, force fade out
            else if (elapsedTime > maxCheckTime) {
                clearInterval(this.fadeCheckInterval);
                this.fadeCheckInterval = null;
                
                // Force the overlay to fade out
                this.transitionOverlay.style.opacity = '0';
                this.transitionOverlay.style.display = 'none';
                document.body.classList.remove('page-transitioning');
                
                // For mobile, try one last direct DOM manipulation approach
                if (this.isMobile) {
                    // Force a style update through a different method
                    requestAnimationFrame(() => {
                        this.transitionOverlay.style.cssText = 'opacity: 0 !important; display: none !important;';
                    });
                }
            }
        }, 100);
    }
}

export default PageTransitions; 